class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // WCAG 1.3.1: remove inert + aria-hidden so drawer headings enter a11y tree when visible
    this.removeAttribute('inert');
    this.removeAttribute('aria-hidden');
    // Resolve-bundle 2026-05-08 evening (a11y P0 #1): mark every top-level body
    // sibling inert so AT cannot Tab into the obscured page. Skip self + script/
    // style nodes. Stash list for symmetric restore in close().
    this._inertedSiblings = [];
    document.body.querySelectorAll(':scope > *').forEach((el) => {
      if (el === this || el.tagName === 'SCRIPT' || el.tagName === 'STYLE') return;
      if (!el.hasAttribute('inert')) {
        el.setAttribute('inert', '');
        this._inertedSiblings.push(el);
      }
    });
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        // Wave 1 fix 2026-05-01: focus close button first (was .drawer__inner
        // container which is not interactive). a11y best-practice: opening
        // dialog moves focus to the dismiss control.
        const focusElement = this.querySelector('.drawer__close')
          || this.querySelector('.drawer__inner');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    // Wave 1 fix 2026-05-01: belt-and-suspenders body scroll lock. The
    // existing .overflow-hidden class addition has no matching CSS rule
    // anywhere in the theme, so background was scrollable behind the
    // drawer. We now also set the inline style as a fallback; CSS rule
    // shipped in component-cart-drawer.css for the canonical path.
    document.body.classList.add('overflow-hidden');
    this._prevBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.classList.remove('active');
    // WCAG 1.3.1: restore inert + aria-hidden so drawer headings exit a11y tree when closed
    this.setAttribute('inert', '');
    this.setAttribute('aria-hidden', 'true');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    // Wave 1 fix 2026-05-01: restore inline overflow set on open.
    document.body.style.overflow = this._prevBodyOverflow || '';
    // Resolve-bundle 2026-05-08 evening (a11y P0 #1): un-inert siblings the
    // open() call marked. Symmetric restore: only touch elements WE marked,
    // never any element that already had inert pre-open.
    if (this._inertedSiblings) {
      this._inertedSiblings.forEach((el) => el.removeAttribute('inert'));
      this._inertedSiblings = null;
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
